import React, { useEffect, useMemo, useState } from "react";
import {
  FiChevronDown,
  FiDownload,
  FiEdit2,
  FiInfo,
  FiMoreVertical,
  FiPlus,
  FiSearch,
  FiSquare,
} from "react-icons/fi";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { LuAlertCircle, LuCheckCircle, LuShield, LuUsers } from "react-icons/lu";
import { auditTrail } from "./PayoutAccessControlData";
import { apiString, normalizeApiStatus } from "./api/payoutApiAdapters";
import { payoutApi } from "./api/payoutApiClient";
import { usePayoutResource } from "./api/usePayoutResource";
import PayoutDataTable from "./PayoutDataTable";
import PayoutDropdown from "./PayoutDropdown";
import PayoutTableCell from "./PayoutTableCell";

type PermissionTuple = [string, string, string, string, string, string];
type ApiRecord = Record<string, unknown>;

const permissionStats = [
  {
    label: "Total Permissions",
    value: "48",
    helper: "All custom & system permissions",
    helperClass: "text-[#505E8E]",
    icon: <LuShield />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Active Permissions",
    value: "41",
    helper: "85.4% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuCheckCircle />,
    tone: "bg-[#16A34A1A] text-[#16A34A]",
  },
  {
    label: "Inactive Permissions",
    value: "7",
    helper: "14.6% of total",
    helperClass: "text-[#505E8E]",
    icon: <LuAlertCircle />,
    tone: "bg-[#F59E0B1A] text-[#F97316]",
  },
  {
    label: "Grouped Permissions",
    value: "12",
    helper: "Logical permission groups",
    helperClass: "text-[#505E8E]",
    icon: <LuUsers />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
];

const categoryClassName: Record<string, string> = {
  Dashboard: "bg-[#6687FF1A] text-[#6687FF]",
  Transfers: "bg-[#DBEAFE] text-[#0284C7]",
  Beneficiaries: "bg-[#DCFCE7] text-[#16A34A]",
  Reports: "bg-[#F59E0B1A] text-[#F97316]",
  "Users & Roles": "bg-[#6687FF1A] text-[#6687FF]",
  "Approval Workflows": "bg-[#FEF3C7] text-[#D97706]",
  Approvals: "bg-[#FEF3C7] text-[#D97706]",
  Administration: "bg-[#E0E7FF] text-[#3730A3]",
  Statements: "bg-[#E5E7EB] text-[#505E8E]",
};

const typeClassName: Record<string, string> = {
  System: "bg-[#E5E7EB] text-[#505E8E]",
  Custom: "bg-[#F3F4F6] text-[#1B163B]",
};

const statusClassName: Record<string, string> = {
  Active: "bg-[#DCFCE7] text-[#16A34A]",
  Inactive: "bg-[#E5E7EB] text-[#505E8E]",
};

const permissionFromApiRecord = (
  record: ApiRecord,
): PermissionTuple => {
  const fallback: PermissionTuple = ["", "", "", "", "", ""];
  const usedInRaw =
    record.roles ||
    record.assigned_roles ||
    record.assignedRoles ||
    record.used_in ||
    record.usedIn;
  const usedIn = Array.isArray(usedInRaw)
    ? usedInRaw
        .map((item) =>
          typeof item === "string"
            ? item
            : apiString(item as ApiRecord, ["name", "role_name", "roleName", "label"]),
        )
        .filter(Boolean)
        .join(", ")
    : apiString(record, ["used_in", "usedIn", "roles", "assigned_roles"], fallback[4]);

  return [
    apiString(record, ["permission_name", "permissionName", "name", "key", "code", "slug"], fallback[0]),
    apiString(record, ["description"], fallback[1]),
    apiString(record, ["category", "group", "module"], fallback[2]),
    apiString(record, ["type", "permission_type", "permissionType"], fallback[3]),
    usedIn || fallback[4],
    normalizeApiStatus(apiString(record, ["status", "state"], fallback[5]), fallback[5]),
  ];
};

function SelectBox({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options?: string[];
  onChange?: (value: string) => void;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      className="min-w-[200px]"
    />
  );
}

function PayoutPermissions() {
  const [showAddPermissionModal, setShowAddPermissionModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All Types");
  const [categoryFilter, setCategoryFilter] = useState("All Categories");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [usedInFilter, setUsedInFilter] = useState("All");
  const [pageSize, setPageSize] = useState("10 per page");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPermission, setSelectedPermission] = useState<string | null>(null);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    type: "All Types",
    category: "All Categories",
    status: "All Status",
    usedIn: "All",
  });
  const rowsPerPage = Number(pageSize.split(" ")[0]) || 10;
  const permissionsResource = usePayoutResource<PermissionTuple[]>(
    [] as PermissionTuple[],
    (signal) =>
      payoutApi.permissions(signal).then((rows) =>
        rows.map((record) => permissionFromApiRecord(record)),
      ),
    [],
  );
  const permissionSourceRows = permissionsResource.data;
  const displayedPermissionStats = useMemo(() => {
    const total = permissionSourceRows.length;
    const active = permissionSourceRows.filter((row) => row[5] === "Active").length;
    const inactive = permissionSourceRows.filter((row) => row[5] === "Inactive").length;
    const grouped = new Set(permissionSourceRows.map((row) => row[2]).filter(Boolean)).size;
    const percent = (value: number) =>
      total > 0 ? `${((value / total) * 100).toFixed(1)}% of total` : "0.0% of total";

    return permissionStats.map((stat) => {
      if (stat.label === "Total Permissions") {
        return { ...stat, value: String(total), helper: "From payouts-service" };
      }
      if (stat.label === "Active Permissions") {
        return { ...stat, value: String(active), helper: percent(active) };
      }
      if (stat.label === "Inactive Permissions") {
        return { ...stat, value: String(inactive), helper: percent(inactive) };
      }
      if (stat.label === "Grouped Permissions") {
        return { ...stat, value: String(grouped), helper: "From payouts-service" };
      }
      return { ...stat, value: "0", helper: "From payouts-service" };
    });
  }, [permissionSourceRows]);
  const filteredPermissionRows = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    return permissionSourceRows.filter((permission) => {
      const matchesSearch =
        !query ||
        permission[0].toLowerCase().includes(query) ||
        permission[1].toLowerCase().includes(query);
      const matchesType =
        appliedFilters.type === "All Types" || permission[3] === appliedFilters.type;
      const matchesCategory =
        appliedFilters.category === "All Categories" ||
        permission[2] === appliedFilters.category;
      const matchesStatus =
        appliedFilters.status === "All Status" || permission[5] === appliedFilters.status;
      const matchesUsedIn =
        appliedFilters.usedIn === "All" || permission[4].includes(appliedFilters.usedIn);

      return (
        matchesSearch &&
        matchesType &&
        matchesCategory &&
        matchesStatus &&
        matchesUsedIn
      );
    });
  }, [appliedFilters, permissionSourceRows]);
  const pageCount = Math.max(1, Math.ceil(filteredPermissionRows.length / rowsPerPage));
  const visiblePermissionRows = filteredPermissionRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const resultStart = filteredPermissionRows.length
    ? (currentPage - 1) * rowsPerPage + 1
    : 0;
  const resultEnd = Math.min(currentPage * rowsPerPage, filteredPermissionRows.length);

  useEffect(() => {
    setAppliedFilters({
      search: searchQuery,
      type: typeFilter,
      category: categoryFilter,
      status: statusFilter,
      usedIn: usedInFilter,
    });
    setCurrentPage(1);
  }, [searchQuery, typeFilter, categoryFilter, statusFilter, usedInFilter]);

  const exportPermissions = () => {
    const headers = ["Permission Name", "Description", "Category", "Type", "Assigned Roles", "Status"];
    const csvRows = filteredPermissionRows.map((permission) =>
      permission.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-permissions.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const permissionTableRows = visiblePermissionRows.map((permission) => [
    <FiSquare className="text-base text-[#CBD3E8]" />,
    <PayoutTableCell
      value={permission[0]}
      valueClassName="font-semibold text-[#1B163B]"
    />,
    <PayoutTableCell
      value={permission[1]}
      valueClassName="font-medium leading-5 text-[#1B163B]"
      maxWidthClassName="max-w-[320px]"
    />,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        categoryClassName[permission[2]]
      }
    >
      {permission[2]}
    </span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        typeClassName[permission[3]]
      }
    >
      {permission[3]}
    </span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        statusClassName[permission[5]]
      }
    >
      {permission[5]}
    </span>,
    <PayoutTableCell
      value={permission[4]}
      valueClassName="font-semibold leading-5 text-[#1B163B]"
      maxWidthClassName="max-w-[260px]"
    />,
    <div className="flex items-center gap-6">
      <button
        type="button"
        onClick={() => setSelectedPermission(permission[0])}
        className="text-[#A3AED0]"
        title="Edit permissions from Role Details"
      >
        <FiEdit2 className="text-base" />
      </button>
      <button
        type="button"
        onClick={() => setSelectedPermission(permission[0])}
        className="text-[#1B163B]"
      >
        <FiMoreVertical className="text-lg" />
      </button>
    </div>,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Permissions
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Manage and configure permissions to control actions across the system.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowAddPermissionModal(true)}
          className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white"
        >
          <FiPlus className="text-lg" />
          Add Permission
          <FiChevronDown />
        </button>
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-4">
        {displayedPermissionStats.map((stat) => (
          <div
            key={stat.label}
            className="flex min-h-[118px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow"
          >
            <div
              className={
                "payout-kpi-icon flex h-11 w-11 shrink-0 items-center justify-center text-xl " +
                stat.tone
              }
            >
              {stat.icon}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#505E8E]">
                {stat.label}
              </p>
              <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
                {stat.value}
              </p>
              <p className={"mt-4 text-xs font-semibold " + stat.helperClass}>
                {stat.helper}
              </p>
            </div>
          </div>
        ))}
      </div>

      <section className="mb-5 rounded-[8px] border border-[#E0E5F2] bg-white px-5 py-5 new-shadow">
        <div className="mb-5 flex gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
          <FiInfo className="mt-0.5 shrink-0 text-[#6687FF]" />
          Permissions are visible only to Level 4 users. Edit permission access from the Role Details screen.
        </div>

        <div className="flex h-10 w-full max-w-[470px] items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-3">
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className="w-full bg-transparent text-sm font-medium text-[#1B163B] outline-none placeholder:text-[#A3AED0]"
            placeholder="Search by permission name or description"
          />
          <FiSearch className="shrink-0 text-[#505E8E]" />
        </div>

        <div className="mt-5 flex flex-wrap items-end gap-4">
          <SelectBox
            label="Permission Type"
            value={typeFilter}
            options={["All Types", "System", "Custom"]}
            onChange={setTypeFilter}
          />
          <SelectBox
            label="Category"
            value={categoryFilter}
            options={["All Categories", "Dashboard", "Statements", "Transfers", "Beneficiaries", "Approvals", "Administration"]}
            onChange={setCategoryFilter}
          />
          <SelectBox
            label="Status"
            value={statusFilter}
            options={["All Status", "Active", "Inactive"]}
            onChange={setStatusFilter}
          />
          <SelectBox
            label="Used In"
            value={usedInFilter}
            options={["All", "Level 1", "Level 2", "Level 3", "Level 4"]}
            onChange={setUsedInFilter}
          />
          <button
            type="button"
            onClick={exportPermissions}
            className="flex h-10 items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
          >
            <FiDownload className="text-lg text-[#505E8E]" />
            Export
          </button>
        </div>
      </section>

      <PayoutDataTable
        columns={[
          "",
          "Permission Name",
          "Description",
          "Category",
          "Type",
          "Status",
          "Assigned Roles",
          "Actions",
        ]}
        rows={permissionTableRows}
        minWidth="min-w-[1080px]"
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {filteredPermissionRows.length} permissions
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <PayoutDropdown
                value={pageSize}
                options={["10 per page", "25 per page", "50 per page"]}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                className="min-w-[120px]"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === page
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                ))}
                {pageCount > 3 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === pageCount
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <section className="hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="w-10 px-5 py-4 text-center">
                  <FiSquare className="text-base text-[#A3AED0]" />
                </th>
                <th className="px-5 py-4 text-center">Permission Name</th>
                <th className="px-5 py-4 text-center">Description</th>
                <th className="px-5 py-4 text-center">Category</th>
                <th className="px-5 py-4 text-center">Type</th>
                <th className="px-5 py-4 text-center">Status</th>
                <th className="px-5 py-4 text-center">Assigned Roles</th>
                <th className="px-5 py-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visiblePermissionRows.map((permission) => (
                <tr
                  key={permission[0]}
                  className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <td className="px-5 py-4">
                    <FiSquare className="text-base text-[#CBD3E8]" />
                  </td>
                  <td className="px-5 py-4 font-semibold">
                    {permission[0]}
                  </td>
                  <td className="max-w-[360px] px-5 py-4 leading-5">
                    {permission[1]}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        categoryClassName[permission[2]]
                      }
                    >
                      {permission[2]}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        typeClassName[permission[3]]
                      }
                    >
                      {permission[3]}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        statusClassName[permission[5]]
                      }
                    >
                      {permission[5]}
                    </span>
                  </td>
                  <td className="max-w-[260px] px-5 py-4 font-semibold leading-5">
                    {permission[4]}
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center justify-end gap-6">
                      <button
                        type="button"
                        onClick={() => setSelectedPermission(permission[0])}
                        className="text-[#A3AED0]"
                        title="Edit permissions from Role Details"
                      >
                        <FiEdit2 className="text-base" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPermission(permission[0])}
                        className="text-[#1B163B]"
                      >
                        <FiMoreVertical className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
          <span className="min-w-max">
            Showing {resultStart} to {resultEnd} of {filteredPermissionRows.length} permissions
          </span>
          <div className="ml-auto flex min-w-max items-center gap-4">
            <PayoutDropdown
              value={pageSize}
              options={["10 per page", "25 per page", "50 per page"]}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
              }}
              className="min-w-[120px]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowLeft className="text-xs" />
              </button>
              {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={
                    "h-9 w-9 rounded-md " +
                    (currentPage === page
                      ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                      : "border border-[#E0E5F2] text-[#1B163B]")
                  }
                >
                  {page}
                </button>
              ))}
              {pageCount > 3 && (
                <>
                  <span className="px-2 text-[#505E8E]">...</span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(pageCount)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === pageCount
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {pageCount}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={currentPage === pageCount}
                onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-5 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <h2 className="text-[18px] font-semibold text-[#1B163B]">
          Permission Audit Trail
        </h2>
        <div className="mt-4 grid gap-3 lg:grid-cols-3">
          {auditTrail.map((item) => (
            <div
              key={item}
              className="rounded-[6px] border border-[#E0E5F2] bg-[#A9B2CF1A] px-4 py-3 text-sm font-medium leading-6 text-[#505E8E]"
            >
              {item}
            </div>
          ))}
        </div>
      </section>
      {(showAddPermissionModal || selectedPermission) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-[460px] rounded-[8px] bg-white p-6 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              {selectedPermission ? "Permission Details" : "Add Permission"}
            </h2>
            <p className="mt-2 text-sm font-medium text-[#505E8E]">
              {selectedPermission
                ? `${selectedPermission} can be managed from Role Details when backend permissions are connected.`
                : "New permission creation will be connected to backend APIs."}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddPermissionModal(false);
                  setSelectedPermission(null);
                }}
                className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddPermissionModal(false);
                  setSelectedPermission(null);
                }}
                className="h-10 rounded-[6px] bg-edviron_black px-6 text-sm font-semibold text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PayoutPermissions;
